'use client';

import React, { useEffect } from 'react';
import { Loading, LoadingProps } from '@/components/ui/loading';
import { selectRecaptcha } from '@/lib/frontend/store/helpers';
import { useAppSelector, useAppStore } from '@/lib/frontend/store/hooks';
import { initGlobalRecaptcha } from '@/lib/frontend/store/actionCreators/GetRecaptchaVerificationToken';

type Props = {
  recaptchaID: string;
  children: React.ReactNode;
  bypass?: boolean;
  loadingProps?: LoadingProps;
};

/*
NB: this component currently only waits for recaptcha to be initialized
before rendering its children, but the way the recaptcha token creation is
implemented, waiting for recaptcha to be initialized before rendering
recaptcha-depending UI elements isn't strictly necessary.

However, recaptcha-dependent UI should still be wrapped in this component, to
ease migration in case we decide to use checkbox recaptcha. This component
serves as a marker and placeholder for where we would need to add the "I'm not
a robot" widgets.
*/
export const ProtectedByRecaptcha = (props: Props) => {
  const { bypass = false, children, loadingProps } = props;
  const store = useAppStore();

  const recaptcha = useAppSelector(selectRecaptcha);

  const enabled = recaptcha.enabled;
  const apiIsAvailable = recaptcha.apiIsAvailable;

  useEffect(() => {
    initGlobalRecaptcha(store);
  }, []);

  // e.g. for checkout UIs where the cart is an invoice
  if (bypass) {
    return children;
  }

  const enableReCAPTCHA = enabled.value;

  if (apiIsAvailable == null) {
    return <Loading {...loadingProps} />;
  }

  if (enableReCAPTCHA && !apiIsAvailable) {
    // go ahead and render. attempts to use a protected action without the recaptcha API loaded will send honeybadger reports
    return children;
  }

  if (enableReCAPTCHA == null) {
    return <Loading {...loadingProps} />;
  }

  return children;
};
